"use client";

import React from "react";
import { EnhancedTabs, EnhancedTabsList, EnhancedTabsTrigger } from "@/components/ui/tabs/enhanced-tabs";
import { ResponsiveSelect } from "@/components/ui/responsive-select";
import type { BaseEntity } from "./UniversalCRMTable/types/shared";
import type { TabConfig } from "./UniversalDetailPage/types/detailPage";

interface DetailModalTabsBarProps<T extends BaseEntity> {
  visibleTabs: TabConfig<T>[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  isMobile: boolean;
}

export function DetailModalTabsBar<T extends BaseEntity>({
  visibleTabs,
  activeTab,
  onTabChange,
  isMobile,
}: DetailModalTabsBarProps<T>) {
  const tabOptions = React.useMemo(
    () => visibleTabs.map((tab) => ({ value: tab.id, label: tab.label })),
    [visibleTabs]
  );

  if (visibleTabs.length === 0) return null;

  if (isMobile) {
    return (
      <div className="mb-4">
        <ResponsiveSelect
          value={activeTab}
          onValueChange={onTabChange}
          options={tabOptions}
          placeholder="Select section"
          className="w-full"
        />
      </div>
    );
  }

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 px-0 py-0 -mx-6 mb-4">
      <div className="px-6">
        <EnhancedTabs value={activeTab} onValueChange={onTabChange}>
          <EnhancedTabsList className="w-full flex flex-row gap-0">
            {visibleTabs.map((tab) => (
              <EnhancedTabsTrigger
                key={tab.id}
                value={tab.id}
                variant={tab.variant || "blue"}
                className="px-3 py-2 text-sm flex-1"
              >
                {tab.label}
              </EnhancedTabsTrigger>
            ))}
          </EnhancedTabsList>
        </EnhancedTabs>
      </div>
    </div>
  );
}
